import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, LayoutDashboard, ArrowRight, Building2, Phone, Clock } from "lucide-react";

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Medications', href: '/#meds' },
  { label: 'How it works', href: '/#how' },
  { label: 'About Us', href: '/about' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

const PawMark = () => (
  <span className="grid place-items-center w-8 h-8 rounded-[10px] bg-paw-primary shadow-[0_6px_14px_-6px_rgba(15,98,214,0.7)]">
    <svg viewBox="0 0 24 24" fill="#fff" className="w-[18px] h-[18px]" aria-hidden="true">
      <circle cx="6" cy="10" r="2.1" /><circle cx="10.5" cy="6.5" r="2.1" /><circle cx="15.5" cy="6.5" r="2.1" /><circle cx="19" cy="10.5" r="1.9" />
      <path d="M12.5 12c-2.4 0-4.4 1.7-5 3.8-.5 1.7.9 3.2 2.7 3.2.9 0 1.6-.4 2.3-.4s1.4.4 2.3.4c1.8 0 3.2-1.5 2.7-3.2-.6-2.1-2.6-3.8-5-3.8z" />
    </svg>
  </span>
);

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openEstimate = () => window.dispatchEvent(new Event('open-estimate-modal'));
  const dashboardHref =
    user?.role === 'admin' ? '/admin'
    : user?.role === 'vet' ? '/vet'
    : user?.role === 'fulfillment_staff' ? '/fulfillment'
    : '/dashboard';
  const isActive = (href) => location.pathname === href;

  return (
    <>
    {/* Contact strip - toll-free number + business hours (very top of every page).
        Background matched to the navy utility bar directly below it (bg-paw-navy #0B2747)
        per Ali. Measured on that navy: white 15.05:1, white/70 8.02:1,
        paw-gold #EBB13E icons 7.80:1 (need 4.5 text / 3.0 icon).
        A white/10 bottom hairline keeps the two navy bars visually separate. */}
    <div className="bg-paw-navy border-b border-white/10" data-testid="contact-strip">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-8 py-1.5 flex flex-wrap items-center justify-center sm:justify-start gap-x-5 gap-y-1 text-[12.5px] font-medium">
        <a href="tel:+18888272717" className="inline-flex items-center gap-1.5 text-white hover:text-paw-gold transition-colors" data-testid="contact-strip-phone">
          <Phone className="w-3.5 h-3.5 text-paw-gold shrink-0" aria-hidden="true" />
          <span className="font-semibold">1-888-827-2717</span>
          <span className="text-white/70 font-normal">Toll Free</span>
        </a>
        <span className="hidden sm:block w-px h-3.5 bg-white/20" aria-hidden="true" />
        <span className="inline-flex items-center gap-1.5 text-white/70" data-testid="contact-strip-hours">
          <Clock className="w-3.5 h-3.5 text-paw-gold shrink-0" aria-hidden="true" />
          Mon-Fri: 8AM to 6PM, Sat: 8AM to 3PM PST
        </span>
      </div>
    </div>
    {/* Top utility bar — Canadian badge (left) + insurance partner entry (right) */}
    <div className="bg-paw-navy text-white">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-8 py-1.5 flex flex-wrap items-center justify-center sm:justify-between gap-x-4 gap-y-1 text-[12.5px] sm:text-[13px] font-medium">
        <span className="inline-flex items-center gap-1.5 text-white/85" data-testid="canadian-badge">
          Proudly a Canadian company
        </span>
        <Link to="/for-insurance" className="group inline-flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5 text-paw-gold shrink-0" />
          <span className="hidden sm:inline text-white/75">Are you an insurance provider?</span>
          <span className="inline-flex items-center gap-1 font-semibold text-white group-hover:text-paw-gold transition-colors">
            Switch to PawLinx for Insurance Referrals
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </Link>
      </div>
    </div>
    <header className="sticky top-0 z-50">
      <div className="px-4 sm:px-6 pt-4">
        <div className={`max-w-[1180px] mx-auto flex items-center gap-4 rounded-full border border-paw-border/70 backdrop-blur-md pl-5 pr-3 transition-all duration-300 ${scrolled ? "bg-white/95 py-2 shadow-[0_10px_30px_-18px_rgba(11,39,71,0.55)]" : "bg-white/80 py-2.5 shadow-[0_18px_40px_-24px_rgba(11,39,71,0.45)]"}`}>
          <Link to="/" className="flex items-center shrink-0">
            <img src="/pawlinx-logo-nav.png" alt="PawLinx" className="h-7 sm:h-8 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 mx-auto">
            {NAV_LINKS.map(({ label, href }) => (
              <Link
                key={label}
                to={href}
                className={`px-3.5 py-2 rounded-full text-[14.5px] transition-colors ${isActive(href) ? 'text-paw-primary bg-paw-blue-50 font-semibold' : 'text-paw-text-secondary font-medium hover:text-paw-navy hover:bg-paw-cream'}`}
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {user ? (
              <>
                <Link to={dashboardHref} className="flex items-center gap-1.5 text-[14.5px] font-medium text-paw-navy hover:text-paw-primary transition-colors">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                <button onClick={logout} className="text-paw-text-secondary hover:text-paw-primary transition-colors" aria-label="Log out">
                  <LogOut className="w-[18px] h-[18px]" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-[14.5px] font-semibold text-paw-navy hover:text-paw-primary transition-colors px-2">Log in</Link>
                <button
                  onClick={openEstimate}
                  className="inline-flex items-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-5 py-2.5 text-[14.5px] font-semibold transition-all shadow-[0_8px_20px_-8px_rgba(15,98,214,0.6)]"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          <button className="lg:hidden ml-auto p-1.5 text-paw-navy" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden max-w-[1180px] mx-auto mt-2 rounded-3xl border border-paw-border/70 bg-white shadow-[0_18px_40px_-24px_rgba(11,39,71,0.45)] p-4">
            <nav className="flex flex-col">
              {NAV_LINKS.map(({ label, href }) => (
                <Link
                  key={label}
                  to={href}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 px-2 text-[15px] font-medium text-paw-text border-b border-paw-border last:border-0"
                >
                  {label}
                </Link>
              ))}
            </nav>
            <div className="mt-4 flex flex-col gap-2">
              {user ? (
                <Link to={dashboardHref} onClick={() => setMobileOpen(false)} className="text-center py-2.5 text-[14.5px] font-semibold text-paw-primary border border-paw-primary rounded-full">
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="text-center py-2.5 text-[14.5px] font-semibold text-paw-navy border border-paw-border rounded-full">
                    Log in
                  </Link>
                  <button
                    onClick={() => { setMobileOpen(false); window.dispatchEvent(new Event('open-estimate-modal')); }}
                    className="py-2.5 text-[14.5px] font-semibold text-white bg-paw-primary rounded-full"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
    </>
  );
}
